import { ApolloError, useMutation } from '@apollo/client';
import { DealRowActionType } from '@openx/components-deal/constants.ts';
import type { DealActionResult, DealRowActionPayload } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import { type OaDealStatus, oaDealStatus } from '@openx/types/deal.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { UpdateDealAndPackageDocument } from '../../../../graphql/mutation/deals/UpdateDealAndPackage.generated.tsx';
import { type SupportedDealActions, useDealsAction } from '../../../../modules/deals/hooks/useDealsAction.tsx';
import { parseApolloFieldErrors } from '../../../../modules/graphql/parseApolloFieldErrors.ts';

const getApolloErrorMessages = (error: unknown): string[] => {
  if (!(error instanceof ApolloError)) return [];

  return parseApolloFieldErrors(error, {}).generalErrors;
};

export function useDealUpdateStatus() {
  const [updateDealAndPackage] = useMutation(UpdateDealAndPackageDocument, {
    refetchQueries: ['GetDealsList', 'GetDeal'],
  });
  const { performDealAction } = useDealsAction();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateDeals = useCallback(
    async ({
      deals,
      status,
      action,
    }: {
      deals: DealRowActionPayload[];
      status: OaDealStatus;
      action: SupportedDealActions;
    }): Promise<DealActionResult> => {
      const filteredDeals = deals.filter(deal => deal.status !== status);
      if (!filteredDeals.length) return Promise.resolve({ fulfilledIds: [] });

      setIsLoading(true);

      try {
        const { fulfilledIds } = await performDealAction({
          action,
          actionHandler: deal =>
            updateDealAndPackage({
              context: { disableGlobalNotification: true },
              variables: { deal: { status }, updateDealAndPackageId: deal.uid },
            }),
          deals: filteredDeals,
          getErrorMessages: getApolloErrorMessages,
        });

        if (fulfilledIds.length) {
          try {
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.DEAL_LANDING] });
          } catch {
            // The landing query owns refresh errors; mutation success remains confirmed.
          }
        }

        return { fulfilledIds };
      } finally {
        setIsLoading(false);
      }
    },
    [performDealAction, queryClient, updateDealAndPackage]
  );

  const pause = useCallback(
    (deals: DealRowActionPayload[]) => {
      return updateDeals({ action: DealRowActionType.PAUSE, deals, status: oaDealStatus.PAUSED });
    },
    [updateDeals]
  );

  const resume = useCallback(
    (deals: DealRowActionPayload[]) => {
      return updateDeals({ action: DealRowActionType.RESUME, deals, status: oaDealStatus.ACTIVE });
    },
    [updateDeals]
  );

  return { isLoading, pause, resume };
}
